import React, { MouseEvent, ReactElement, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { parse } from 'b2b-apps/shop/common/utils/queryParams';
import { ProductVariantsComponent } from 'b2b-apps/shop/product/components/ProductData/ProductVariants/index';
import { ProductVariantOptionType, ProductVariantType } from 'b2b-apps/shop/product/redux/products/types';
import { ROUTES } from 'b2b-apps/shop/routes/components/Router';
import { useOpenLink, useUrlCreator } from 'b2b-apps/shop/routes/hooks';
import { getCdnImageUrl, getProductFallbackImgUrl } from 'b2b-apps/shop/routes/utils/url';

const addThumbnailPlaceholder = (groups: ProductVariantType[]): ProductVariantType[] => groups.map(group => {
  const hasThumbnails = group.options.some(o => !!o.thumbnail);
  const fallbackUrl = hasThumbnails ? getProductFallbackImgUrl() : null;

  return {
    ...group,
    options: group.options.map(option => (
      {
        ...option,
        thumbnail: getCdnImageUrl(option.thumbnail) || fallbackUrl,
      }
    )),
  };
});

interface WithContainerProps {
  sku: string;
  variantGroups: ProductVariantType[];
}

export const WithContainer = ({ sku, variantGroups }: WithContainerProps): ReactElement => {
  const location = useLocation();
  const urlCreator = useUrlCreator();
  const openLink = useOpenLink();

  const onSelect = (event: MouseEvent<HTMLInputElement>, option: ProductVariantOptionType): void => {
    if (option.link?.sku === sku) {
      return;
    }

    const urlToProduct = urlCreator(ROUTES.product, { sku: option.link?.sku });
    const isExactMatchPart = !option.link?.match ? '?variants=no-match' : '';

    openLink(event, `${urlToProduct}${isExactMatchPart}`);
  };

  const hasSelectionError = useMemo(() => {
    if (location.search) {
      const query = parse(location.search);
      return query?.variants === 'no-match';
    }

    return false;
  }, [location.search]);

  const groupsWithThumbnails = addThumbnailPlaceholder(variantGroups);

  return (
    <ProductVariantsComponent
      // this key is important - without that component doesn't rerender when we go back to previous product
      key={sku}
      variantGroups={groupsWithThumbnails}
      hasSelectionError={hasSelectionError}
      onSelect={onSelect}
    />
  );
};
